export interface HeatItem {
  rank: number;
  title: string;
  tag: string;
  heat: string;
  trend: 'up' | 'down' | 'flat';
  delta: string;
  isHot?: boolean;
}

export interface FocusCard {
  id: number;
  label: string;
  title: string;
  lead: string;
  reads: string;
  comments: string;
  image: string;
  time: string;
}

export interface DiscussionPost {
  id: number;
  name: string;
  role: string;
  avatar: string;
  text: string;
  likes: string;
  replies: string;
  hot: boolean;
  time: string;
  tags: string[];
}

export interface RisingItem {
  title: string;
  base: string;
  target: string;
  percent: string;
  note: string;
}

export interface Brief {
  time: string;
  text: string;
  tag: string;
}

export const heroLead = {
  issue: 'VOL.091',
  kicker: '今日焦点 · 实时热度追踪',
  title: '暗流涌动的热度周报：谁在悄悄升温，谁已跌落风口',
  summary:
    '过去 24 小时，多则91黑料话题热度快速抬升。我们从讨论量、转发曲线与路人口碑三线交叉比对，为你拆出这一轮真正在发酵的瓜料，以及几近退潮的旧闻。',
  meta: '2026.09.06 · 舆情编辑部 · 数据截至 21:30',
  reads: '128万',
  comments: '3.2万',
  shares: '5.6千',
  image:
    'https://readdy.ai/api/search-image?query=Warm%20editorial%20magazine%20photography%20of%20a%20dimly%20lit%20theater%20stage%20with%20a%20single%20spotlight%20glowing%20against%20dark%20maroon%20curtains%2C%20cinematic%20atmosphere%2C%20moody%20amber%20and%20ink%20tones%2C%20film%20grain%2C%20restrained%20minimal%20composition%20leaving%20ample%20negative%20space%2C%20high%20contrast%2C%20premium%20print%20quality&width=900&height=1200&seq=editorial-lead-2026&orientation=portrait',
};

export const heatList: HeatItem[] = [
  { rank: 1, title: '片场深夜事件发酵，导演回应被指避重就轻', tag: '热议中', heat: '982万', trend: 'up', delta: '48万', isHot: true },
  { rank: 2, title: '综艺录制现场大翻车，嘉宾表情管理失控', tag: '热搜', heat: '764万', trend: 'up', delta: '23万', isHot: true },
  { rank: 3, title: '当红艺人直播口误引旧事重提，评论区炸锅', tag: '上升', heat: '612万', trend: 'up', delta: '11万' },
  { rank: 4, title: '热播剧大结局被提前剧透，官微深夜发声', tag: '争议', heat: '588万', trend: 'down', delta: '-7万' },
  { rank: 5, title: '某经纪公司声明引猜测，粉丝后援会连夜表态', tag: '焦点', heat: '433万', trend: 'flat', delta: '+2万' },
  { rank: 6, title: '真人秀片段遭剪辑质疑，正片与花絮对不上', tag: '关注', heat: '377万', trend: 'up', delta: '9万' },
  { rank: 7, title: '跨年晚会嘉宾名单外泄，合作方紧急澄清', tag: '攀升', heat: '299万', trend: 'up', delta: '15万' },
  { rank: 8, title: '品牌方终止合作传闻，代言评论区两极分化', tag: '商战', heat: '251万', trend: 'down', delta: '-3万' },
  { rank: 9, title: '老牌艺人重提多年前恩怨，直播观看翻三倍', tag: '怀旧', heat: '188万', trend: 'up', delta: '6万' },
  { rank: 10, title: '新剧番位之争升级，制片人深夜发文疑似内涵', tag: '观察', heat: '142万', trend: 'flat', delta: '+1万' },
];

export const focusCards: FocusCard[] = [
  {
    id: 1,
    label: '专题深挖',
    title: '一场直播事故如何引爆三天的热搜连击',
    lead: '从一句口误到全员下场，我们逐帧复盘事件如何在12小时内完成三次翻烧，热度曲线呈现罕见的三波峰。',
    reads: '98万',
    comments: '2.1万',
    time: '2026.09.06',
    image:
      'https://readdy.ai/api/search-image?query=Editorial%20still%20of%20a%20modern%20broadcast%20studio%20control%20room%20with%20muted%20screens%20glowing%20amber%20and%20warm%20cream%20tones%2C%20cables%20and%20mixing%20desk%2C%20soft%20shadows%2C%20restrained%20cinematic%20composition%20with%20negative%20space%2C%20premium%20print%20editorial%20aesthetic&width=1000&height=700&seq=focus-live-2026&orientation=landscape',
  },
  {
    id: 2,
    label: '数据拆解',
    title: '同一事件，为何微博、短视频、讨论组三地冷热完全不同',
    lead: '同一个瓜在不同平台走向分裂。我们对比了发帖密度、评论情绪与话题生命周期，找出真正的意见场中心。',
    reads: '76万',
    comments: '1.4万',
    time: '2026.09.06',
    image:
      'https://readdy.ai/api/search-image?query=Warm%20editorial%20flat%20lay%20photography%20of%20a%20newspaper%20spread%20with%20data%20charts%20and%20ink%20graphs%20on%20cream%20paper%2C%20reading%20glasses%20and%20a%20pen%2C%20soft%20natural%20window%20light%2C%20amber%20and%20mustard%20accents%2C%20minimal%20negative%20space%2C%20high%20end%20editorial%20composition&width=1000&height=700&seq=focus-data-2026&orientation=landscape',
  },
  {
    id: 3,
    label: '来龙去脉',
    title: '从一句声明开始的误会：番位之争完整时间线',
    lead: '一张海报、两封声明、三次热搜。我们把整起事件按小时重排，还原每个关键节点的真实音量与失真环节。',
    reads: '61万',
    comments: '9800',
    time: '2026.09.06',
    image:
      'https://readdy.ai/api/search-image?query=Editorial%20photography%20of%20a%20timeline%20collage%20with%20clipped%20newspaper%20headlines%20and%20photographs%20pinned%20on%20a%20warm%20beige%20mood%20board%2C%20string%20and%20washi%20tape%2C%20soft%20directional%20light%2C%20warm%20cream%20and%20maroon%20tones%2C%20artful%20negative%20space%2C%20premium%20print%20feel&width=1000&height=700&seq=focus-timeline-2026&orientation=landscape',
  },
  {
    id: 4,
    label: '人物侧写',
    title: '风口浪尖上的他，为何选择沉默而非澄清',
    lead: '当争议铺天盖地，当事人一反常态地低调。多位业内人士与我们聊了聊公关策略里的「冷处理」与「赌一把」。',
    reads: '53万',
    comments: '7200',
    time: '2026.09.05',
    image:
      'https://readdy.ai/api/search-image?query=Atmospheric%20editorial%20portrait%20concept%20of%20a%20silhouetted%20figure%20standing%20alone%20in%20a%20dim%20warm%20room%2C%20face%20not%20visible%2C%20back%20lit%20by%20soft%20amber%20window%20light%2C%20dark%20ink%20shadows%2C%20moody%20film%20grain%2C%20minimal%20and%20restrained%2C%20premium%20editorial%20composition&width=1000&height=700&seq=focus-person-2026&orientation=landscape',
  },
  {
    id: 5,
    label: '情绪地图',
    title: '评论区风向一晚三变，我们跟踪了情绪如何漂移',
    lead: '同一事件的情绪从愤怒到嘲讽再到反转到中立只用了十个小时。追踪情绪拐点，往往比追踪事实本身更有信息量。',
    reads: '41万',
    comments: '6500',
    time: '2026.09.05',
    image:
      'https://readdy.ai/api/search-image?query=Abstract%20editorial%20composition%20of%20overlapping%20speech%20bubbles%20and%20comment%20threads%20in%20warm%20cream%20and%20amber%20palette%20with%20dark%20ink%20outlines%2C%20paper%20texture%2C%20soft%20shadows%2C%20layered%20depth%2C%20restrained%20modern%20print%20design%2C%20lots%20of%20negative%20space&width=1000&height=700&seq=focus-sentiment-2026&orientation=landscape',
  },
  {
    id: 6,
    label: '前瞻研判',
    title: '三天后热度会退潮吗？我们用历史模型做了预测',
    lead: '相似事件的衰减曲线告诉我们答案。结合转发结构与被炒热的手段，判断这波讨论还能撑多久、下一轮拐点在哪。',
    reads: '38万',
    comments: '4300',
    time: '2026.09.05',
    image:
      'https://readdy.ai/api/search-image?query=Minimal%20editorial%20still%20life%20of%20a%20chessboard%20with%20a%20few%20chess%20pieces%20in%20warm%20amber%20and%20cream%20tones%2C%20dramatic%20soft%20side%20lighting%2C%20deep%20ink%20shadows%2C%20film%20grain%2C%20restrained%20strategic%20composition%2C%20premium%20print%20aesthetic&width=1000&height=700&seq=focus-predict-2026&orientation=landscape',
  },
];

export const categories = ['全部', '综艺现场', '影视剧集', '直播名场面', '商业动态', '艺人动向', '路人口碑'];

export const topicChips: string[] = [
  '片场翻车', '直播事故', '剧集剧透', '番位之争', '公关声明', '品牌解约', '旧事重提', '二创爆火', '粉丝表态',
];

export const discussionPosts: DiscussionPost[] = [
  {
    id: 1,
    name: '夜航西飞',
    role: '8级冲浪选手',
    avatar: '夜',
    text: '这次直播那几句口误我看得满头问号，前因后果都没交代，网友就直接给他定了性，瓜还没吃完就开始审判了。',
    likes: '1.2万',
    replies: '386',
    hot: true,
    time: '12分钟前',
    tags: ['直播事故', '评论区审判'],
  },
  {
    id: 2,
    name: '一颗荔枝壳',
    role: '理性吃瓜代表',
    avatar: '荔',
    text: '我翻完整段录播了，其实是断章取义占多数。但团队第二天那个回应是真离谱，等于亲手把热度又点着了。',
    likes: '8600',
    replies: '214',
    hot: true,
    time: '35分钟前',
    tags: ['声明翻车', '逐帧分析'],
  },
  {
    id: 3,
    name: '半糖不加冰',
    role: '剧粉',
    avatar: '半',
    text: '大结局被提前剧透这事真没法忍，等了三个月一个晚上被全毁了。以后这种剧我干脆养肥了再看。',
    likes: '5400',
    replies: '176',
    hot: false,
    time: '1小时前',
    tags: ['影视剧集', '剧透'],
  },
  {
    id: 4,
    name: '南山无所有',
    role: '娱乐观察员',
    avatar: '南',
    text: '别只看热搜有多热闹，要看热搜是怎么被买上去的。这两天的榜单结构明显不对劲，有机的还是人工的能看出来。',
    likes: '9200',
    replies: '298',
    hot: true,
    time: '2小时前',
    tags: ['榜单结构', '营销分析'],
  },
  {
    id: 5,
    name: '一勺盐',
    role: '老牌路人',
    avatar: '盐',
    text: '从老艺人翻旧账到品牌连夜解约，我发现舆论最怕的不是黑料本身，而是「等一等」三个字。信息差就是放大镜。',
    likes: '4100',
    replies: '121',
    hot: false,
    time: '3小时前',
    tags: ['旧事重提', '信息差'],
  },
  {
    id: 6,
    name: '云上有只鹿',
    role: '数据党',
    avatar: '云',
    text: '把评论区情绪按小时拆开看很有意思，从愤怒到嘲讽再到反转让位，整个曲线像坐了趟过山车，拐点都在证据反转那几秒。',
    likes: '6800',
    replies: '203',
    hot: false,
    time: '4小时前',
    tags: ['情绪漂移', '反转'],
  },
];

export const risingList: RisingItem[] = [
  { title: '剧组发布会延期', base: '当前热度 38万', target: '预测峰值 210万', percent: '+182%', note: '多方入场，讨论面快速扩大' },
  { title: '某艺人旧照考古', base: '当前热度 27万', target: '预测峰值 150万', percent: '+160%', note: '考古向内容天然带转发力' },
  { title: '直播切片二次爆火', base: '当前热度 19万', target: '预测峰值 98万', percent: '+138%', note: '切片流被推上首页' },
  { title: '品牌代言悬念', base: '当前热度 15万', target: '预测峰值 72万', percent: '+117%', note: '舆论蓄势待发' },
  { title: '跨年名单猜测', base: '当前热度 12万', target: '预测峰值 61万', percent: '+108%', note: '后续官宣将带来二次引爆' },
];

export const briefs: Brief[] = [
  { time: '21:18', text: '某导演深夜发文回应片场事件，删删改改被截图存档。', tag: '声明' },
  { time: '20:52', text: '热播剧官微上线结局防剧透功能，此前剧透词条登顶。', tag: '剧集' },
  { time: '20:30', text: '直播平台更新防口误提示规则，网友戏称「晚到了」。#91黑料', tag: '平台' },
  { time: '20:07', text: '品牌方就解约传闻发声明：谈判仍在进行中，勿传谣。', tag: '商业' },
  { time: '19:45', text: '跨年晚会合作方否认名单外泄，称一切以官宣为准。', tag: '娱乐' },
  { time: '19:21', text: '路人拍摄的片场花絮视频两小时播放破百万。', tag: '现场' },
  { time: '18:58', text: '粉丝后援会连夜发布联合声明，呼吁理性发言。', tag: '粉丝' },
  { time: '18:33', text: '业内人士爆料冷处理内幕，引发二次讨论高峰。', tag: '内幕' },
];

export const footerNav = [
  { title: '热度追踪', links: ['实时热搜', '七日榜单', '周度报告'] },
  { title: '内容分区', links: ['综艺现场', '影视剧集', '直播名场面'] },
  { title: '关于我们', links: ['编辑团队', '数据口径', '联系投稿'] },
];